import type { AppConfig, ChatMessage } from "../types";

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
  }
}

async function parseErrorMessage(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: string };
    if (body.error) return body.error;
  } catch {
    // ignore
  }
  return `Chyba servera (${response.status}).`;
}

async function request(path: string, init?: RequestInit): Promise<Response> {
  const response = await fetch(path, { ...init, credentials: "include" });
  if (!response.ok) {
    throw new ApiError(await parseErrorMessage(response), response.status);
  }
  return response;
}

export async function login(password: string): Promise<void> {
  await request("/api/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ password }),
  });
}

export async function logout(): Promise<void> {
  await request("/api/logout", { method: "POST" });
}

export async function getSession(): Promise<{ authenticated: boolean }> {
  const response = await request("/api/session");
  return response.json();
}

export async function getConfig(): Promise<AppConfig> {
  const response = await request("/api/config");
  return response.json();
}

export async function transcribe(audio: Blob, filename: string): Promise<string> {
  const form = new FormData();
  form.append("audio", audio, filename);
  const response = await request("/api/transcribe", { method: "POST", body: form });
  const data = (await response.json()) as { text: string };
  return data.text;
}

export async function chat(messages: ChatMessage[]): Promise<string> {
  const response = await request("/api/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      messages: messages.map((m) => ({ role: m.role, content: m.content })),
    }),
  });
  const data = (await response.json()) as { reply: string };
  return data.reply;
}

export async function speak(text: string, voice: string): Promise<Blob> {
  const response = await request("/api/speech", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text, voice }),
  });
  return response.blob();
}
